import { type LookupResponse, lookupRef } from '@/lib/api/reports-client.js';
import { useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 300;

export function useRefLookup(params: {
  view: string;
  labelField: string;
  valueField: string;
  q: string;
  enabled?: boolean;
}) {
  const [data, setData] = useState<LookupResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Generation counter — incremented on every new query; stale responses are discarded
  const genRef = useRef(0);

  useEffect(() => {
    if (params.enabled === false) return;

    if (timerRef.current !== null) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      // Abort the previous in-flight network request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Capture the generation for this request
      genRef.current += 1;
      const myGen = genRef.current;

      setIsLoading(true);
      setError(null);
      try {
        const result = await lookupRef(
          {
            view: params.view,
            labelField: params.labelField,
            valueField: params.valueField,
            q: params.q,
            pageSize: 20,
          },
          controller.signal,
        );
        // Discard result if a newer query has already fired
        if (myGen !== genRef.current) return;
        setData(result);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (myGen !== genRef.current) return;
        setError(err instanceof Error ? err : new Error('Lookup failed'));
      } finally {
        if (myGen === genRef.current) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, [params.view, params.labelField, params.valueField, params.q, params.enabled]);

  return { data, isLoading, error };
}
